import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import TextareaAutosize from "react-textarea-autosize";
import { Square } from "lucide-react";
import { onValue, ref as dbRef } from "firebase/database";
import { getDownloadURL, ref as fileRef, uploadBytesResumable } from "firebase/storage";
import { database, storage } from "../../../helpers/firebaseConfig";
import BuilderHelpDialog from "./BuilderHelpDialog";
import { trackBuilderEvent, trackBuilderFailure } from "../../../helpers/builderAnalytics";
import { agentRequest, cancelAgentJob, completeAgentFile, formatAgentError, isAgentCancellationEvent, registerAgentFile, stopAgentTurn } from "./api/agentApi";
import { deriveAgentActivity } from "./agentActivity";
import { latestCausalFinding, methodEntries } from "./analysisMethods";
import "./WuiAgentChat.css";

const ACTIVE = new Set(["queued", "running", "waiting_for_approval"]);
const EMPTY_JOBS = [];
const SESSION_PAGE_SIZE = 20;
const ANALYSIS_STAGES = new Set([
  "starting", "downloading_inputs", "inspecting_files", "writing_code", "running_code",
  "running_agent", "uploading_artifacts", "presenting_result", "completed_with_warnings",
]);
const EXTENSIONS = new Set(["csv", "json", "geojson", "tif", "tiff", "gpkg", "zip", "docx", "txt", "md"]);
const MAX_SIZE = 250 * 1024 * 1024;
const AGENT_UNAVAILABLE_MESSAGE = "The agent is unavailable. This may be due to a connection issue or service outage. Try again later, start a new chat, or contact support.";
const LABELS = {
  mcp__wui_simulations__create_simulation_run: "Create simulation run",
  mcp__wui_simulations__execute_simulation_run: "Execute simulation run",
};
const JOB_STAGE_LABELS = {
  queued: "Waiting for worker",
  starting: "Starting",
  downloading_inputs: "Downloading inputs",
  inspecting_files: "Inspecting files",
  writing_code: "Writing analysis code",
  running_code: "Running analysis",
  running_agent: "Agent working",
  presenting_result: "Presenting findings",
  uploading_artifacts: "Uploading outputs",
  completed: "Completed",
  completed_with_warnings: "Completed with warnings",
  waiting_for_approval: "Waiting for approval",
  waiting_for_user: "Waiting for input",
  failed: "Failed",
  timed_out: "Timed out",
  cancelled: "Cancelled",
};
const array = (value) => Object.entries(value || {}).map(([id, row]) => ({ id, ...row }));
const bytes = (n) => n == null || n === "" || !Number.isFinite(Number(n)) || Number(n) <= 0 ? "" : Number(n) < 1024 ? `${n} B` : Number(n) < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`;
const toolLabel = (name) => LABELS[name] || String(name || "Agent action").replace(/^mcp__[^_]+__/, "").replaceAll("_", " ");
const uploadStageLabel = (stage) => ({
  selected: "Preparing",
  registering: "Preparing",
  uploading: "Uploading",
  confirming: "Finishing",
  registration_failed: "Upload failed",
  upload_failed: "Upload failed",
  confirmation_failed: "Upload failed",
}[stage || "selected"] || "Preparing");
const jobText = (job) => ({
  queued: "Waiting to start",
  running: job.progressMessage || "Agent is processing the request",
  waiting_for_approval: "Waiting for approval",
  completed: job.outcome?.summary || "Processing completed",
  failed: job.outcome?.summary || "Processing failed",
  timed_out: job.outcome?.summary || "Processing exceeded its time limit",
  cancelled: job.outcome?.summary || "Processing cancelled",
}[job.status] || job.progressMessage || "Processing request");
const stageLabel = (job) => job.status === "cancelled" ? "Cancelled" : JOB_STAGE_LABELS[job.stage] || JOB_STAGE_LABELS[job.status] || String(job.stage || job.status || "Processing").replaceAll("_", " ");
const isWarningJob = (job) => job.stage === "completed_with_warnings" || job.outcome?.status === "COMPLETED_WITH_WARNINGS";

function jobActivity(job) {
  return Object.values(job.activity || {}).sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
}

function isAnalysisJob(job) {
  if (job?.analysis || ANALYSIS_STAGES.has(job?.stage)) return true;
  const descriptor = `${job?.type || ""} ${job?.kind || ""} ${job?.jobType || ""} ${job?.workerType || ""} ${job?.mode || ""}`;
  if (/analysis|analyst/i.test(descriptor)) return true;
  return jobActivity(job).some((entry) => /simulation analyst|direct simulation analysis|data-contract\.md|01_plan\.md|02_analysis\.py/i.test(`${entry.label || ""} ${entry.detail || ""}`));
}

function jobLiveness(job) {
  if (!ACTIVE.has(job.status) || !job.lastHeartbeatAt) return "";
  const heartbeatAge = Date.now() - Number(job.lastHeartbeatAt);
  const activityAge = job.lastActivityAt ? Date.now() - Number(job.lastActivityAt) : 0;
  if (heartbeatAge > 60000) return "Worker connection may be interrupted";
  if (activityAge > 90000) return "Still running a long operation";
  if (heartbeatAge <= 30000) return "Worker connected";
  return "Worker responding slowly";
}

function recordTimestamp(record) {
  const raw = record?.createdAt ?? record?.timestamp ?? record?.ts;
  if (raw == null) return null;
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function renderInlineMarkdown(text) {
  return String(text || "").split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={index}>{part.slice(2, -2)}</strong>;
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function tableCells(line) {
  return String(line).trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

function isTableDivider(line) {
  const cells = tableCells(line);
  return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function AgentMessageContent({ text }) {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let prose = [];
  const flushProse = () => {
    if (!prose.length) return;
    const value = prose.join("\n");
    blocks.push(<div className="agent-messageText" key={`text-${blocks.length}`}>{renderInlineMarkdown(value)}</div>);
    prose = [];
  };
  for (let index = 0; index < lines.length;) {
    if (lines[index].includes("|") && index + 1 < lines.length && isTableDivider(lines[index + 1])) {
      flushProse();
      const headers = tableCells(lines[index]);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        rows.push(tableCells(lines[index]));
        index += 1;
      }
      blocks.push(<div className="agent-tableScroll" key={`table-${blocks.length}`}><table><thead><tr>{headers.map((cell, cellIndex) => <th key={cellIndex}>{renderInlineMarkdown(cell)}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{headers.map((_, cellIndex) => <td key={cellIndex}>{renderInlineMarkdown(row[cellIndex] || "")}</td>)}</tr>)}</tbody></table></div>);
    } else {
      prose.push(lines[index]);
      index += 1;
    }
  }
  flushProse();
  return blocks;
}

export default function WuiAgentChat({
  simulationId,
  userId = "",
  onSessionChange,
  presentations = [],
  onActivatePresentation,
  onFocusPresentationMessage,
  onAnalysisJobsChange,
  onOpenAnalysis,
  onLatestQuestionChange,
}) {
  const key = `wui-agent-session:${simulationId}`;
  const [sessions, setSessions] = useState([]), [sessionId, setSessionId] = useState(() => localStorage.getItem(key) || "");
  const [sessionMetadata, setSessionMetadata] = useState({ sessionId: "", data: null }), [messages, setMessages] = useState([]), [approvals, setApprovals] = useState([]);
  const [events, setEvents] = useState([]);
  const [files, setFiles] = useState([]), [sessionJobs, setSessionJobs] = useState({ sessionId: "", data: [] }), [artifacts, setArtifacts] = useState([]), [uploads, setUploads] = useState({});
  const [draft, setDraft] = useState(""), [error, setError] = useState("");
  const [loading, setLoading] = useState(true), [submittingSessionIds, setSubmittingSessionIds] = useState({}), [creating, setCreating] = useState(false);
  const [stoppingSessionIds, setStoppingSessionIds] = useState({}), [stopAcceptedSessionIds, setStopAcceptedSessionIds] = useState({}), [stopErrors, setStopErrors] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false), [workspaceOpen, setWorkspaceOpen] = useState(false), [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState(""), [resolving, setResolving] = useState({}), [cancelling, setCancelling] = useState({}), [downloading, setDownloading] = useState({});
  const [workspacePortal, setWorkspacePortal] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState("inputs");
  const [visibleSessionCount, setVisibleSessionCount] = useState(SESSION_PAGE_SIZE);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const inputRef = useRef(null), logRef = useRef(null), tasksRef = useRef(new Map());
  const submittingSessionsRef = useRef(new Set());
  const stoppingSessionsRef = useRef(new Set());
  const stopAcceptedSessionsRef = useRef(new Set());
  const stoppedSessionsRef = useRef(new Set());
  const selectedSessionRef = useRef(sessionId);
  selectedSessionRef.current = sessionId;
  const metadata = sessionMetadata.sessionId === sessionId ? sessionMetadata.data : null;
  const jobs = sessionJobs.sessionId === sessionId ? sessionJobs.data : EMPTY_JOBS;
  const activeJob = jobs.find((job) => ACTIVE.has(job.status));
  const submitting = !!submittingSessionIds[sessionId];
  const stopping = !!stoppingSessionIds[sessionId];
  const stopAccepted = !!stopAcceptedSessionIds[sessionId];
  const chatLocked = !!sessionId && (submitting || !!metadata?.activeTurnId);
  useEffect(() => {
    if (!sessionId || chatLocked || activeJob || !stopAccepted) return;
    stopAcceptedSessionsRef.current.delete(sessionId);
    setStopAcceptedSessionIds((current) => {
      const next = { ...current };
      delete next[sessionId];
      return next;
    });
  }, [sessionId, chatLocked, activeJob, stopAccepted]);
  const requestedCancellationRef = useRef("");
  const isNearBottomRef = useRef(true);
  const encodedSim = encodeURIComponent(simulationId);
  const draftStorageKey = useCallback(
    (id) => id ? `wui-agent-draft:${simulationId}:${id}` : "",
    [simulationId]
  );
  const readStoredDraft = useCallback((id) => {
    const storageKey = draftStorageKey(id);
    if (!storageKey) return "";
    try { return sessionStorage.getItem(storageKey) || ""; } catch (_) { return ""; }
  }, [draftStorageKey]);
  const storeDraft = useCallback((id, value) => {
    const storageKey = draftStorageKey(id);
    if (!storageKey) return;
    try {
      if (value) sessionStorage.setItem(storageKey, value);
      else sessionStorage.removeItem(storageKey);
    } catch (_) {}
  }, [draftStorageKey]);
  const setUpload = (id, patch) => setUploads((old) => ({ ...old, [id]: { ...old[id], ...patch } }));

  const artifactsByJob = useMemo(() => {
    const grouped = {};
    jobs.forEach((job) => { grouped[job.id] = (job.outcome?.artifacts || []).map((a) => ({ ...a, id: a.id || a.artifactId })); });
    artifacts.forEach((a) => { grouped[a.jobId] ||= []; if (!grouped[a.jobId].some((x) => x.id === a.id)) grouped[a.jobId].push(a); });
    return grouped;
  }, [jobs, artifacts]);
  const presentationsByMessage = useMemo(() => {
    const grouped = {};
    presentations.forEach((item) => {
      const messageId = String(item?.messageId || "");
      if (!messageId) return;
      (grouped[messageId] ||= []).push(item);
    });
    return grouped;
  }, [presentations]);
  const timeline = useMemo(() => {
    if (!messages.length) return jobs.map((job) => ({ type: "job", id: `job:${job.id}`, job }));
    const jobsAfterMessage = new Map();
    jobs.forEach((job) => {
      const resultMessageId = job.outcome?.messageId;
      let anchorIndex = resultMessageId
        ? messages.findIndex((message) => message.id === resultMessageId)
        : -1;
      if (anchorIndex < 0) {
        const createdAt = recordTimestamp(job);
        if (createdAt != null) {
          let nearestTime = -Infinity;
          messages.forEach((message, index) => {
            const messageTime = recordTimestamp(message);
            if (messageTime != null && messageTime <= createdAt && messageTime >= nearestTime) {
              anchorIndex = index;
              nearestTime = messageTime;
            }
          });
        }
      }
      if (anchorIndex < 0) anchorIndex = messages.length - 1;
      const anchored = jobsAfterMessage.get(anchorIndex) || [];
      anchored.push(job);
      anchored.sort((a, b) => (recordTimestamp(a) || 0) - (recordTimestamp(b) || 0));
      jobsAfterMessage.set(anchorIndex, anchored);
    });
    return messages.flatMap((message, index) => [
      { type: "message", id: `message:${message.id}`, message },
      ...(jobsAfterMessage.get(index) || []).map((job) => ({ type: "job", id: `job:${job.id}`, job })),
    ]);
  }, [jobs, messages]);
  const messageActivityKey = useMemo(() => messages.map((message) => `${message.id}:${message.sequence}:${message.text}`).join("|"), [messages]);
  const jobActivityKey = useMemo(() => jobs.map((job) => [
    job.id,
    job.status,
    job.stage,
    job.progressMessage,
    job.cancelRequested,
    job.outcome?.status,
    job.outcome?.summary,
    jobActivity(job).at(-1)?.sequence,
    methodEntries(job.analysis?.methods?.steps).length,
    job.analysis?.methods?.updatedAt,
    ...(job.outcome?.artifacts || []).map((artifact) => artifact.id),
  ].join(":" )).join("|"), [jobs]);
  const approvalActivityKey = useMemo(() => approvals.map((approval) => `${approval.id}:${approval.status}`).join("|"), [approvals]);
  const artifactActivityKey = useMemo(() => artifacts.map((artifact) => `${artifact.id}:${artifact.status}:${artifact.size}`).join("|"), [artifacts]);
  const activity = useMemo(() => deriveAgentActivity(events, approvals, toolLabel), [events, approvals]);
  const deniedActiveTurn = !!metadata?.activeTurnId && approvals.some((approval) => approval.status === "denied" && approval.turnId === metadata.activeTurnId);
  const showActivity = !stopAccepted && !!((metadata?.activeTurnId && !deniedActiveTurn) || activeJob);
  const activityLabel = metadata?.activeTurnId && activity?.turnId === String(metadata.activeTurnId)
    ? activity.label : activeJob ? stageLabel(activeJob) : "Agent is working…";
  const analysisJobs = useMemo(() => jobs
    .filter(isAnalysisJob)
    .map((job) => ({ ...job, artifacts: artifactsByJob[job.id] || [] })), [jobs, artifactsByJob]);

  useEffect(() => { onAnalysisJobsChange?.({ sessionId, jobs: analysisJobs }); }, [analysisJobs, onAnalysisJobsChange, sessionId]);
  const visibleSessions = useMemo(() => {
    const recent = sessions.slice(0, visibleSessionCount);
    if (!sessionId || recent.some((session) => session.id === sessionId)) return recent;
    const selected = sessions.find((session) => session.id === sessionId);
    return selected ? [selected, ...recent] : recent;
  }, [sessions, sessionId, visibleSessionCount]);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentRequest(`/model-builders/${encodedSim}/chat-sessions`), next = data.sessions || [];
      setSessions(next); setSessionId((old) => old && next.some((s) => s.id === old) ? old : next[0]?.id || "");
    } catch (e) { trackBuilderFailure("builder_chat_load_failed", { builder_id: simulationId, tab: "chat", endpoint: "chat_sessions" }, e); setError(formatAgentError(e, AGENT_UNAVAILABLE_MESSAGE)); } finally { setLoading(false); }
  }, [encodedSim]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { setVisibleSessionCount(SESSION_PAGE_SIZE); }, [simulationId]);
  useEffect(() => { setWorkspacePortal(document.getElementById("agent-session-workspace-root")); }, []);
  useEffect(() => { sessionId ? localStorage.setItem(key, sessionId) : localStorage.removeItem(key); }, [key, sessionId]);
  useLayoutEffect(() => { onSessionChange?.(sessionId); }, [onSessionChange, sessionId]);
  useEffect(() => { setDraft(readStoredDraft(sessionId)); }, [readStoredDraft, sessionId]);

  useEffect(() => {
    setSessionMetadata({ sessionId, data: null }); setMessages([]); setApprovals([]); setEvents([]); setFiles([]); setSessionJobs({ sessionId, data: [] }); setArtifacts([]); setUploads({}); setError("");
    requestedCancellationRef.current = "";
    tasksRef.current.forEach((task) => task.cancel()); tasksRef.current.clear();
    if (!sessionId) return undefined;
    const base = `model_builders/${simulationId}/chat_sessions/${sessionId}`, fail = (e) => { trackBuilderFailure("builder_chat_stream_failed", { builder_id: simulationId, tab: "chat", chat_id: sessionId, endpoint: "chat_stream" }, e); setError(formatAgentError(e)); };
    let knownEventIds = null;
    const handleLiveEvent = (event) => {
      if (event.type === "turn.completed") {
        requestedCancellationRef.current = "";
        trackBuilderEvent("builder_chat_response_received", { builder_id: simulationId, tab: "chat", chat_id: sessionId });
      }
      if (["turn.failed", "turn.error"].includes(event.type)) {
        if (isAgentCancellationEvent(event, requestedCancellationRef.current)) {
          requestedCancellationRef.current = "";
          return;
        }
        setError(formatAgentError(event.error || event.message || "The task couldn’t finish."));
        trackBuilderEvent("builder_chat_turn_failed", { builder_id: simulationId, tab: "chat", chat_id: sessionId, error_code: "turn_failed" });
      }
    };
    const subscriptions = [
      onValue(dbRef(database, `${base}/metadata`), (s) => setSessionMetadata({ sessionId, data: s.val() }), fail),
      onValue(dbRef(database, `${base}/messages`), (s) => {
        const nextMessages = array(s.val()).sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setMessages(nextMessages);
        const latestQuestion = [...nextMessages].reverse().find((message) => message.role === "user");
        onLatestQuestionChange?.({ sessionId, at: latestQuestion ? recordTimestamp(latestQuestion) : null });
      }, fail),
      onValue(dbRef(database, `${base}/approvals`), (s) => setApprovals(array(s.val())), fail),
      onValue(dbRef(database, `${base}/files`), (s) => setFiles(array(s.val())), fail),
      onValue(dbRef(database, `${base}/jobs`), (s) => setSessionJobs({ sessionId, data: array(s.val()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0)) }), fail),
      onValue(dbRef(database, `${base}/artifacts`), (s) => setArtifacts(array(s.val())), fail),
      onValue(dbRef(database, `${base}/events`), (snapshot) => {
        const eventRows = array(snapshot.val()).sort((a, b) => Number(a.sequence || a.createdAt || 0) - Number(b.sequence || b.createdAt || 0));
        setEvents(eventRows);
        if (knownEventIds === null) {
          knownEventIds = new Set(eventRows.map((event) => event.id));
          return;
        }
        eventRows.forEach((event) => {
          if (knownEventIds.has(event.id)) return;
          knownEventIds.add(event.id);
          handleLiveEvent(event);
        });
      }, fail),
    ];
    return () => subscriptions.forEach((fn) => fn());
  }, [simulationId, sessionId, onLatestQuestionChange]);
  const scrollToLatest = useCallback((behavior = "smooth") => {
    const element = logRef.current;
    if (!element) return;
    isNearBottomRef.current = true;
    setHasNewActivity(false);
    element.scrollTo({ top: element.scrollHeight, behavior });
  }, []);
  const handleMessageScroll = () => {
    const element = logRef.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 80;
    if (isNearBottomRef.current) setHasNewActivity(false);
  };
  useEffect(() => {
    if (isNearBottomRef.current) scrollToLatest("auto");
    else setHasNewActivity(true);
  }, [messageActivityKey, jobActivityKey, approvalActivityKey, artifactActivityKey, activity, scrollToLatest]);
  useEffect(() => {
    isNearBottomRef.current = true;
    setHasNewActivity(false);
  }, [sessionId]);

  async function createSession(e) {
    e.preventDefault(); setCreating(true); setError("");
    try { const r = await agentRequest(`/model-builders/${encodedSim}/chat-sessions`, { method: "POST", body: JSON.stringify({ title: title.trim() || "New conversation", userId }) }); await loadSessions(); setSessionId(r.sessionId); setDialogOpen(false); setTitle(""); trackBuilderEvent("builder_chat_created", { builder_id: simulationId, tab: "chat", chat_id: r.sessionId }); }
    catch (x) { trackBuilderFailure("builder_chat_create_failed", { builder_id: simulationId, tab: "chat", endpoint: "chat_sessions" }, x); setError(formatAgentError(x)); } finally { setCreating(false); }
  }
  async function send(e) {
    e.preventDefault(); const message = draft.trim(); const sendingSessionId = sessionId;
    if (!message || !sendingSessionId || submittingSessionsRef.current.has(sendingSessionId) || stoppingSessionsRef.current.has(sendingSessionId) || stopAcceptedSessionsRef.current.has(sendingSessionId) || metadata?.activeTurnId) return;
    onLatestQuestionChange?.({ sessionId: sendingSessionId, at: Date.now() });
    submittingSessionsRef.current.add(sendingSessionId);
    stoppedSessionsRef.current.delete(sendingSessionId);
    setSubmittingSessionIds((current) => ({ ...current, [sendingSessionId]: true }));
    isNearBottomRef.current = true; setHasNewActivity(false);
    requestedCancellationRef.current = "";
    setDraft(""); storeDraft(sendingSessionId, ""); setError("");
    try {
      await agentRequest(`/model-builders/${encodedSim}/chat-sessions/${encodeURIComponent(sendingSessionId)}/turns`, { method: "POST", withAuth: true, body: JSON.stringify({ message, userId }) });
      trackBuilderEvent("builder_chat_message_sent", { builder_id: simulationId, tab: "chat", chat_id: sendingSessionId, message_length: message.length });
      try { await loadSessions(); }
      catch (refreshError) { setError(formatAgentError(refreshError)); }
    }
    catch (x) {
      if (stoppedSessionsRef.current.has(sendingSessionId)) return;
      trackBuilderFailure("builder_chat_send_failed", { builder_id: simulationId, tab: "chat", chat_id: sendingSessionId, endpoint: "chat_turns" }, x);
      const latestQuestion = [...messages].reverse().find((item) => item.role === "user");
      onLatestQuestionChange?.({ sessionId: sendingSessionId, at: latestQuestion ? recordTimestamp(latestQuestion) : null });
      if (selectedSessionRef.current === sendingSessionId) {
        setDraft((current) => {
          const restored = current ? `${message}\n${current}` : message;
          storeDraft(sendingSessionId, restored);
          return restored;
        });
      } else {
        const current = readStoredDraft(sendingSessionId);
        storeDraft(sendingSessionId, current ? `${message}\n${current}` : message);
      }
      setError(x.status === 409 ? "This conversation is currently processing another request. Wait for it to finish, then try again." : formatAgentError(x, AGENT_UNAVAILABLE_MESSAGE));
    }
    finally {
      stoppedSessionsRef.current.delete(sendingSessionId);
      submittingSessionsRef.current.delete(sendingSessionId);
      setSubmittingSessionIds((current) => {
        const next = { ...current };
        delete next[sendingSessionId];
        return next;
      });
    }
  }
  async function stop() {
    const stoppingSessionId = sessionId;
    if (!stoppingSessionId || !chatLocked || stoppingSessionsRef.current.has(stoppingSessionId) || stopAcceptedSessionsRef.current.has(stoppingSessionId)) return;
    stoppingSessionsRef.current.add(stoppingSessionId);
    setStoppingSessionIds((current) => ({ ...current, [stoppingSessionId]: true }));
    setStopErrors((current) => ({ ...current, [stoppingSessionId]: "" }));
    setError("");
    try {
      await stopAgentTurn(simulationId, stoppingSessionId);
      if (submittingSessionsRef.current.has(stoppingSessionId)) stoppedSessionsRef.current.add(stoppingSessionId);
      stopAcceptedSessionsRef.current.add(stoppingSessionId);
      setStopAcceptedSessionIds((current) => ({ ...current, [stoppingSessionId]: true }));
    } catch (x) {
      setStopErrors((current) => ({ ...current, [stoppingSessionId]: formatAgentError(x, AGENT_UNAVAILABLE_MESSAGE) }));
    } finally {
      stoppingSessionsRef.current.delete(stoppingSessionId);
      setStoppingSessionIds((current) => {
        const next = { ...current };
        delete next[stoppingSessionId];
        return next;
      });
    }
  }
  async function resolve(id, decision) {
    setResolving((x) => ({ ...x, [id]: true }));
    try { await agentRequest(`/model-builders/${encodedSim}/chat-sessions/${encodeURIComponent(sessionId)}/approvals/${encodeURIComponent(id)}`, { method: "POST", withAuth: true, body: JSON.stringify({ decision }) }); }
    catch (x) { setError(formatAgentError(x)); setResolving((v) => ({ ...v, [id]: false })); }
  }
  async function confirm(upload) {
    setUpload(upload.key, { stage: "confirming", error: "" });
    try { const completed = await completeAgentFile(upload.simulationId, upload.sessionId, upload.fileId); setUpload(upload.key, { stage: "ready", completed }); trackBuilderEvent("builder_chat_file_attached", { builder_id: simulationId, tab: "chat", chat_id: upload.sessionId, file_type: upload.file?.name?.split(".").pop()?.toLowerCase(), file_size_bytes: upload.file?.size }); }
    catch (x) { trackBuilderFailure("builder_chat_upload_failed", { builder_id: simulationId, tab: "chat", chat_id: upload.sessionId, endpoint: "file_complete" }, x); setUpload(upload.key, { stage: "confirmation_failed", error: x.message }); }
  }
  function transfer(upload) {
    setUpload(upload.key, { stage: "uploading", error: "" });
    const task = uploadBytesResumable(fileRef(storage, upload.storagePath), upload.file, { contentType: upload.file.type || "application/octet-stream" }); tasksRef.current.set(upload.key, task);
    task.on("state_changed", (s) => setUpload(upload.key, { progress: s.totalBytes ? s.bytesTransferred / s.totalBytes : 0 }), (x) => { tasksRef.current.delete(upload.key); if (x.code !== "storage/canceled") { trackBuilderFailure("builder_chat_upload_failed", { builder_id: simulationId, tab: "chat", chat_id: upload.sessionId, endpoint: "file_storage" }, x); setUpload(upload.key, { stage: "upload_failed", error: x.message }); } }, () => { tasksRef.current.delete(upload.key); confirm(upload); });
  }
  async function register(upload) {
    setUpload(upload.key, { stage: "registering", error: "" });
    try { const registered = { ...upload, ...(await registerAgentFile(upload.simulationId, upload.sessionId, upload.file)) }; setUpload(upload.key, registered); transfer(registered); }
    catch (x) { trackBuilderFailure("builder_chat_upload_failed", { builder_id: simulationId, tab: "chat", chat_id: upload.sessionId, endpoint: "file_register" }, x); setUpload(upload.key, { stage: "registration_failed", error: x.message }); }
  }
  function selectFiles(e) {
    const selected = [...(e.target.files || [])]; e.target.value = "";
    selected.forEach((file) => { const extension = file.name.toLowerCase().split(".").pop(); if (!EXTENSIONS.has(extension)) return setError(`${file.name} is not a supported staging file type.`); if (file.size > MAX_SIZE) return setError(`${file.name} exceeds the 250 MB staging upload limit.`); const id = `${Date.now()}-${Math.random()}`, upload = { key: id, file, name: file.name, size: file.size, simulationId, sessionId, progress: 0 }; setUpload(id, upload); register(upload); setWorkspaceOpen(true); });
  }
  function retry(upload) { if (upload.stage === "registration_failed") register(upload); else if (upload.stage === "upload_failed") transfer(upload); else confirm(upload); }
  async function cancel(jobId) {
    requestedCancellationRef.current = jobId;
    setCancelling((x) => ({ ...x, [jobId]: true }));
    try { await cancelAgentJob(simulationId, sessionId, jobId); }
    catch (x) {
      requestedCancellationRef.current = "";
      setError(formatAgentError(x));
      setCancelling((v) => ({ ...v, [jobId]: false }));
    }
  }
  async function download(a) { setDownloading((x) => ({ ...x, [a.id]: true })); try { window.open(await getDownloadURL(fileRef(storage, a.storagePath)), "_blank", "noopener,noreferrer"); } catch (x) { setError(formatAgentError(x)); } finally { setDownloading((x) => ({ ...x, [a.id]: false })); } }

  const pendingUploads = Object.values(uploads).filter((u) => u.stage !== "ready");
  const activeUploads = pendingUploads.filter((u) => ["selected", "registering", "uploading", "confirming"].includes(u.stage || "selected"));
  const failedUploads = pendingUploads.filter((u) => String(u.stage || "").endsWith("_failed"));
  const pendingFileIds = new Set(pendingUploads.map((u) => u.fileId).filter(Boolean));
  const visibleFiles = files.filter((f) => f.status === "ready" && !pendingFileIds.has(f.id));
  const displayedError = stopErrors[sessionId] || error;
  const workspace = <section className="agent-workspace">
    <div className="agent-workspaceHeader">
      <button className="agent-workspaceToggle" onClick={() => setWorkspaceOpen(!workspaceOpen)}>
        <span>Session workspace</span>
        <small>{visibleFiles.length} ready{activeUploads.length ? ` · ${activeUploads.length} uploading` : ""}{failedUploads.length ? ` · ${failedUploads.length} failed` : ""}</small>
      </button>
      <button className="mb-btn mb-btnSecondary agent-uploadButton" onClick={() => inputRef.current?.click()}>Upload files</button>
      <input ref={inputRef} className="agent-fileInput" type="file" multiple accept={[...EXTENSIONS].map((x) => `.${x}`).join(",")} onChange={selectFiles}/>
    </div>
    {workspaceOpen && <div className="agent-workspaceList">
      <div className="agent-workspaceTabs" role="tablist" aria-label="Workspace files">
        <button type="button" role="tab" aria-selected={workspaceTab === "inputs"} className={workspaceTab === "inputs" ? "isActive" : ""} onClick={() => setWorkspaceTab("inputs")}>Uploaded files <span>{visibleFiles.length}</span></button>
        <button type="button" role="tab" aria-selected={workspaceTab === "outputs"} className={workspaceTab === "outputs" ? "isActive" : ""} onClick={() => setWorkspaceTab("outputs")}>Generated outputs <span>{artifacts.length}</span></button>
      </div>
      {workspaceTab === "inputs" ? <>
        {!visibleFiles.length && !pendingUploads.length && <div className="agent-empty">No completed uploaded files.</div>}
        {visibleFiles.map((f) => <div className="agent-fileRow" key={f.id}><div><strong>{f.name}</strong><small>{bytes(f.size)} · {f.id.slice(0, 8)}</small></div><span className={`agent-status is-${f.status}`}>{f.status}</span></div>)}
        {pendingUploads.map((u) => <div className="agent-fileRow" key={u.key}><div><strong>{u.name}</strong><small>{bytes(u.size)}{bytes(u.size) ? " · " : ""}{uploadStageLabel(u.stage)}</small>{u.stage === "uploading" && <progress value={u.progress} max="1"/>}{u.error && <em>{u.error}</em>}</div>{u.stage?.endsWith("_failed") && <button className="mb-btn mb-btnSecondary" onClick={() => retry(u)}>Retry</button>}</div>)}
        <div className="agent-uploadHint">Staging file types only; maximum 250 MB each.</div>
      </> : <>
        {!artifacts.length && <div className="agent-empty">No generated outputs yet.</div>}
        {artifacts.map((artifact) => <div className="agent-fileRow" key={artifact.id}><div><strong>{artifact.name}</strong><small>{artifact.relativePath && artifact.relativePath !== artifact.name ? `${artifact.relativePath} · ` : ""}{bytes(artifact.size)}{artifact.jobId ? ` · Job ${artifact.jobId.slice(0, 8)}` : ""}</small></div><span className={`agent-status is-${artifact.status}`}>{artifact.status}</span><button className="mb-btn mb-btnSecondary" disabled={downloading[artifact.id]} onClick={() => download(artifact)}>{downloading[artifact.id] ? "Opening…" : "Download"}</button></div>)}
      </>}
    </div>}
  </section>;
  return <div className="agent-shell"><div className={`agent-chat ${historyOpen ? "" : "isHistoryCollapsed"}`}>
    <aside className="agent-chatSidebar"><button className="agent-historyToggle" type="button" aria-label={historyOpen ? "Close previous conversations" : "Open previous conversations"} aria-expanded={historyOpen} onClick={() => setHistoryOpen(!historyOpen)}><span>{historyOpen ? "‹" : "›"}</span>{historyOpen && <strong>Previous conversations</strong>}</button>{historyOpen && <div className="agent-historyContent"><button className="mb-btn agent-newChat" onClick={() => setDialogOpen(true)}>+ New conversation</button><div className="agent-sessionList">{loading && <div className="agent-empty">Loading…</div>}{visibleSessions.map((s) => <button key={s.id} className={`agent-session ${s.id === sessionId ? "isActive" : ""}`} onClick={() => { if (s.id !== sessionId) trackBuilderEvent("builder_chat_switched", { builder_id: simulationId, tab: "chat", chat_id: s.id }); setSessionId(s.id); }}><strong>{s.title || "New conversation"}</strong><span>{s.lastMessage || "Start a conversation"}</span></button>)}{visibleSessionCount < sessions.length && <button className="agent-loadMore" type="button" onClick={() => setVisibleSessionCount((count) => Math.min(count + SESSION_PAGE_SIZE, sessions.length))}>Load more <span>{sessions.length - visibleSessionCount} remaining</span></button>}</div></div>}</aside>
    <section className="agent-chatMain">{!loading && !sessionId ? <div className="agent-noConversations"><button onClick={() => setDialogOpen(true)}><span>+</span></button><strong>Create conversation</strong><button className="agent-emptyHelpButton" type="button" onClick={() => setHelpOpen(true)}>Help</button></div> : <>
      <div className="agent-messageLog" ref={logRef} onScroll={handleMessageScroll}>
        {!messages.length && <div className="agent-welcome">Ask about simulation runs, upload files, or request an action.</div>}
        {timeline.map((item) => item.type === "message" ? (
          <div
            key={item.id}
            className={`agent-message ${item.message.role === "user" ? "isUser" : "isAssistant"} ${(presentationsByMessage[item.message.id] || []).length ? "hasResults" : ""}`}
            onClick={() => {
              const linked = presentationsByMessage[item.message.id] || [];
              const target = linked.find((result) =>
                result.kind === "map" && result.status === "ready"
              ) || linked[0];
              if (target) onActivatePresentation?.(target.id, item.message.id);
            }}
          >
            <span>{item.message.role === "user" ? "You" : "WUI-GO Agent"}</span><div><AgentMessageContent text={item.message.text} />
              {(presentationsByMessage[item.message.id] || []).map((result) => (
                <button className="agent-resultCard" type="button" key={result.id} onClick={(event) => { event.stopPropagation(); onActivatePresentation?.(result.id, item.message.id); }}>
                  <span><strong>{result.title || result.id}</strong><small>{result.kind || "result"}{result.status === "preparing" ? " · preparing" : ""}</small></span>
                  <em>Show →</em>
                </button>
              ))}
            </div>
          </div>
        ) : (() => {
          const job = item.job;
          const activityItems = jobActivity(job);
          const liveness = jobLiveness(job);
          const analysisJob = isAnalysisJob(job);
          const latestFinding = analysisJob ? latestCausalFinding(job.analysis?.methods) : null;
          return <div className={`agent-job is-${job.status} ${isWarningJob(job) ? "hasWarning" : ""}`} key={item.id}>
            <div className="agent-jobHeader"><strong>Processing request</strong><span>{stageLabel(job)}</span></div>
            <div>{job.progressMessage || jobText(job)}</div>
            {job.elapsedSeconds != null && <small>{Math.max(0, Number(job.elapsedSeconds)).toLocaleString()}s elapsed</small>}
            {liveness && <small className="agent-jobLiveness">{liveness}</small>}
            {isWarningJob(job) && job.outcome?.warning && <div className="agent-jobWarning">Warning: {String(job.outcome.warning).replaceAll("_", " ")}</div>}
            <div className="agent-jobActions">
              {analysisJob ? <button className="mb-btn mb-btnSecondary agent-jobLiveStatus" type="button" onClick={() => onOpenAnalysis?.(job, ["completed", "completed_with_warnings"].includes(job.status) && job.analysis?.methods?.status === "complete" ? "methods" : "activity")}><span className={ACTIVE.has(job.status) ? "isActive" : job.status === "cancelled" ? "isCancelled" : ""} />{stageLabel(job)} · View live analysis →</button>
                : activityItems.length > 0 && <button className="mb-btn mb-btnSecondary" type="button" onClick={() => setExpandedJobs((old) => ({ ...old, [job.id]: !old[job.id] }))}>{expandedJobs[job.id] ? "Hide activity" : `View activity (${activityItems.length})`}</button>}
              {ACTIVE.has(job.status) && <button className="mb-btn mb-btnSecondary" disabled={cancelling[job.id]} onClick={() => cancel(job.id)}>{cancelling[job.id] || job.cancelRequested ? "Cancellation requested…" : "Cancel"}</button>}
            </div>
            {latestFinding && <div className="agent-jobFinding" role="button" tabIndex={0} aria-label="Open Methods for latest analysis finding" onClick={() => onOpenAnalysis?.(job, "methods")} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpenAnalysis?.(job, "methods"); } }}>
              <div className="agent-jobFindingHeader"><strong>Latest finding</strong><span>Step {latestFinding.number}</span></div>
              {latestFinding.rootCause && <p>{latestFinding.rootCause}</p>}
              {!latestFinding.found && latestFinding.testing && <div><strong>Testing</strong><span>{latestFinding.testing}</span></div>}
              {latestFinding.found && <div><strong>Found</strong><span>{latestFinding.found}</span></div>}
              {latestFinding.next && <div><strong>Next</strong><span>{latestFinding.next}</span></div>}
            </div>}
            {!analysisJob && expandedJobs[job.id] && <div className="agent-jobActivity">{activityItems.map((entry, index) => <div key={entry.id || index} className="agent-jobActivityRow"><span>{index === activityItems.length - 1 && ACTIVE.has(job.status) ? "●" : "✓"}</span><div><strong>{entry.label || stageLabel(entry)}</strong>{entry.detail && <small>{entry.detail}</small>}{entry.createdAt && <time>{new Date(entry.createdAt).toLocaleTimeString()}</time>}</div></div>)}</div>}
            {!analysisJob && (artifactsByJob[job.id] || []).slice(0, 5).map((a) => <div className="agent-artifact" key={a.id}><div><strong>{a.name}</strong><small>{bytes(a.size)}</small></div><button className="mb-btn mb-btnSecondary" disabled={downloading[a.id]} onClick={() => download(a)}>Download</button></div>)}
            {!analysisJob && (artifactsByJob[job.id] || []).length > 5 && <div className="agent-artifactOverflow">+{artifactsByJob[job.id].length - 5} more outputs available in Session workspace.</div>}
          </div>;
        })())}
        {showActivity && <div className="agent-activity"><i/>{activityLabel}</div>}
        {approvals.filter((a) => a.status === "pending").map((a) => <div className="agent-approval" key={a.id}><strong>Approval required</strong><div>{toolLabel(a.toolName)}</div><pre>{JSON.stringify(a.toolInput || {}, null, 2)}</pre><div className="agent-approvalActions"><button className="mb-btn" disabled={resolving[a.id]} onClick={() => resolve(a.id, "allow")}>Allow</button><button className="mb-btn mb-btnDanger" disabled={resolving[a.id]} onClick={() => resolve(a.id, "deny")}>Deny</button></div></div>)}
      </div>
      {hasNewActivity && <button className="agent-scrollToLatest" type="button" onClick={() => scrollToLatest()}>New activity ↓</button>}
      {displayedError && <div className={`agent-error ${displayedError === "The task couldn’t finish." ? "isNeutral" : ""}`} role="status"><span>{displayedError}</span><button type="button" onClick={() => { setError(""); setStopErrors((current) => ({ ...current, [sessionId]: "" })); }} aria-label="Dismiss notice" title="Dismiss notice">×</button></div>}
      <form className="agent-composer" onSubmit={send}>
        <TextareaAutosize value={draft} onChange={(e) => { setDraft(e.target.value); storeDraft(sessionId, e.target.value); }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form.requestSubmit(); } }} disabled={!sessionId} placeholder="Message the WUI-GO agent…" minRows={2} maxRows={12}/>
        <div className="agent-composerActions">
          <div className="agent-composerLeading"><button className="agent-attachButton" type="button" onClick={() => inputRef.current?.click()} aria-label="Upload files" title="Upload files">+</button><button className="agent-helpButton" type="button" onClick={() => setHelpOpen(true)}>Help</button></div>
          {chatLocked || stopping || stopAccepted
            ? <button className="agent-stopButton" type="button" onClick={stop} disabled={stopping || stopAccepted} aria-label={stopping || stopAccepted ? "Stopping request" : "Stop active request"} title={stopping || stopAccepted ? "Stopping…" : "Stop"}><Square size={16} fill="currentColor" aria-hidden="true" /></button>
            : <button className="agent-sendButton" type="submit" disabled={!draft.trim() || !sessionId} aria-label="Send message" title="Send">↑</button>}
        </div>
      </form>
    </>}</section></div>
    {sessionId && workspacePortal && createPortal(workspace, workspacePortal)}
    {helpOpen && <BuilderHelpDialog chatId={sessionId} onClose={() => setHelpOpen(false)} />}
    {dialogOpen && <div className="agent-dialogBackdrop" onMouseDown={() => !creating && setDialogOpen(false)}><form className="agent-createDialog" onSubmit={createSession} onMouseDown={(e) => e.stopPropagation()}><strong>New conversation</strong><label>Conversation name</label><input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus/><div className="agent-dialogActions"><button type="button" className="mb-btn mb-btnSecondary" onClick={() => setDialogOpen(false)}>Cancel</button><button className="mb-btn" disabled={creating}>{creating ? "Creating…" : "Create"}</button></div></form></div>}
  </div>;
}
